// src/components/organizer/OrganizerActivityWidget.js
import React, { useState } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Typography,
  Box,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Tabs,
  Tab,
  Divider,
  Button,
  Badge
} from '@mui/material';
import {
  PersonAdd as RegisterIcon,
  Event as EventIcon,
  Payment as PaymentIcon,
  Edit as EditIcon,
  Email as EmailIcon,
  Notifications as NotificationIcon,
  MoreVert as MoreVertIcon,
  CheckCircle as ApprovalIcon,
  Cancel as CancelIcon,
  TrendingUp as GrowthIcon,
  Schedule as ScheduleIcon,
  Assessment as ReportIcon
} from '@mui/icons-material';

const OrganizerActivityWidget = ({ data }) => {
  const [tabValue, setTabValue] = useState(0);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedActivity, setSelectedActivity] = useState(null);

  if (!data || !Array.isArray(data)) {
    const mockData = [
      { 
        id: 1, 
        type: 'new_registration', 
        description: 'Juan Pérez se inscribió en Maratón Ciudad', 
        timestamp: new Date(Date.now() - 30 * 60 * 1000).toISOString(),
        metadata: { eventName: 'Maratón Ciudad', participantName: 'Juan Pérez', amount: 150 }
      },
      { 
        id: 2, 
        type: 'payment_received', 
        description: 'Pago confirmado: Ana García - Copa Juvenil', 
        timestamp: new Date(Date.now() - 45 * 60 * 1000).toISOString(),
        metadata: { eventName: 'Copa Juvenil', participantName: 'Ana García', amount: 75 }
      },
      { 
        id: 3, 
        type: 'event_updated', 
        description: 'Actualizaste el horario del Triatlón Regional', 
        timestamp: new Date(Date.now() - 60 * 60 * 1000).toISOString(),
        metadata: { eventName: 'Triatlón Regional', field: 'schedule' }
      },
      { 
        id: 4, 
        type: 'approval_required', 
        description: 'María López requiere aprobación manual', 
        timestamp: new Date(Date.now() - 90 * 60 * 1000).toISOString(),
        metadata: { eventName: 'Maratón Ciudad', participantName: 'María López', reason: 'special_category' }
      },
      { 
        id: 5, 
        type: 'milestone_reached', 
        description: 'Maratón Ciudad alcanzó 400 participantes', 
        timestamp: new Date(Date.now() - 120 * 60 * 1000).toISOString(),
        metadata: { eventName: 'Maratón Ciudad', milestone: '400_participants' }
      }
    ];
    data = mockData;
  }

  const getActivityIcon = (type) => {
    const icons = {
      new_registration: <RegisterIcon />,
      payment_received: <PaymentIcon />,
      event_updated: <EditIcon />,
      approval_required: <NotificationIcon />,
      milestone_reached: <GrowthIcon />,
      email_sent: <EmailIcon />,
      event_created: <EventIcon />,
      report_generated: <ReportIcon />
    };
    return icons[type] || <NotificationIcon />;
  };

  const getActivityColor = (type) => {
    const colors = {
      new_registration: 'success',
      payment_received: 'success',
      event_updated: 'info',
      approval_required: 'warning',
      milestone_reached: 'primary',
      email_sent: 'info',
      event_created: 'primary',
      report_generated: 'secondary'
    };
    return colors[type] || 'default';
  };

  const getActivityTitle = (type) => {
    const titles = {
      new_registration: 'Nueva Inscripción',
      payment_received: 'Pago Recibido',
      event_updated: 'Evento Actualizado',
      approval_required: 'Requiere Aprobación',
      milestone_reached: 'Hito Alcanzado',
      email_sent: 'Email Enviado',
      event_created: 'Evento Creado',
      report_generated: 'Reporte Generado'
    };
    return titles[type] || 'Actividad';
  };

  const getPriorityLevel = (type) => {
    const priorities = {
      approval_required: 'high',
      payment_received: 'medium',
      new_registration: 'medium',
      milestone_reached: 'medium',
      event_updated: 'low',
      email_sent: 'low'
    };
    return priorities[type] || 'low';
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInMinutes = Math.floor((now - date) / (1000 * 60));
    
    if (diffInMinutes < 1) return 'Ahora mismo';
    if (diffInMinutes < 60) return `Hace ${diffInMinutes}m`;
    if (diffInMinutes < 1440) return `Hace ${Math.floor(diffInMinutes / 60)}h`;
    return date.toLocaleDateString('es-ES', { 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Filtrar actividades por prioridad
  const allActivities = data || [];
  const highPriorityActivities = allActivities.filter(a => getPriorityLevel(a.type) === 'high');
  const recentActivities = allActivities.filter(a => {
    const activityDate = new Date(a.timestamp);
    const hourAgo = new Date(Date.now() - 60 * 60 * 1000);
    return activityDate > hourAgo;
  });

  const getCurrentActivities = () => {
    switch (tabValue) {
      case 0: return allActivities.slice(0, 8);
      case 1: return highPriorityActivities.slice(0, 8);
      case 2: return recentActivities.slice(0, 8);
      default: return allActivities.slice(0, 8);
    }
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleMenuOpen = (event, activity) => {
    setAnchorEl(event.currentTarget);
    setSelectedActivity(activity);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedActivity(null);
  };

  const handleActivityAction = (action) => {
    console.log(`${action} actividad:`, selectedActivity);
    handleMenuClose();
  };

  const currentActivities = getCurrentActivities();

  return (
    <Card sx={{ height: '100%' }}>
      <CardHeader
        title="📋 Actividad Reciente"
        subheader={`${allActivities.length} eventos registrados`}
        action={
          <Box display="flex" gap={1}>
            {highPriorityActivities.length > 0 && (
              <Badge badgeContent={highPriorityActivities.length} color="warning">
                <NotificationIcon />
              </Badge>
            )}
            <Button size="small" variant="text">
              Ver Todo
            </Button>
          </Box>
        }
      />
      
      <CardContent>
        {/* Alertas de alta prioridad */}
        {highPriorityActivities.length > 0 && (
          <Box sx={{ mb: 2, p: 1, bgcolor: 'warning.light', borderRadius: 1 }}>
            <Typography variant="caption" color="warning.dark">
              ⚠️ {highPriorityActivities.length} actividad(es) requieren tu atención
            </Typography>
          </Box>
        )}

        {/* Tabs para diferentes vistas */}
        <Tabs value={tabValue} onChange={handleTabChange} sx={{ mb: 2 }}>
          <Tab 
            label={`Todas (${allActivities.length})`} 
            sx={{ fontSize: '0.75rem' }}
          />
          <Tab 
            label={`Urgentes (${highPriorityActivities.length})`} 
            sx={{ fontSize: '0.75rem' }}
          />
          <Tab 
            label={`Recientes (${recentActivities.length})`} 
            sx={{ fontSize: '0.75rem' }}
          />
        </Tabs>

        {/* Lista de actividades */}
        <List dense sx={{ maxHeight: 350, overflow: 'auto' }}>
          {currentActivities.map((activity, index) => (
            <React.Fragment key={activity.id || index}>
              <ListItem
                alignItems="flex-start"
                secondaryAction={
                  <Box display="flex" alignItems="center" gap={1}>
                    <Typography variant="caption" color="text.secondary">
                      {formatTimestamp(activity.timestamp)}
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={(e) => handleMenuOpen(e, activity)}
                    >
                      <MoreVertIcon fontSize="small" />
                    </IconButton>
                  </Box>
                }
              >
                <ListItemAvatar>
                  <Avatar 
                    sx={{ 
                      bgcolor: `${getActivityColor(activity.type)}.main`,
                      width: 32,
                      height: 32
                    }}
                  >
                    {React.cloneElement(getActivityIcon(activity.type), { fontSize: 'small' })}
                  </Avatar>
                </ListItemAvatar>
                
                <ListItemText
                  primary={
                    <Box display="flex" alignItems="center" gap={1}>
                      <Typography variant="body2" fontWeight="bold">
                        {getActivityTitle(activity.type)}
                      </Typography>
                      {getPriorityLevel(activity.type) === 'high' && (
                        <Chip 
                          label="Urgente" 
                          color="warning" 
                          size="small"
                          sx={{ fontSize: '0.6rem', height: 16 }}
                        />
                      )}
                    </Box>
                  }
                  secondary={
                    <Box>
                      <Typography variant="body2" color="text.primary">
                        {activity.description}
                      </Typography>
                      {activity.metadata && (
                        <Typography variant="caption" color="text.secondary">
                          {activity.metadata.eventName && `Evento: ${activity.metadata.eventName}`}
                          {activity.metadata.amount && ` • $${activity.metadata.amount}`}
                        </Typography>
                      )}
                    </Box>
                  }
                />
              </ListItem>
              
              {index < currentActivities.length - 1 && <Divider variant="inset" component="li" />}
            </React.Fragment>
          ))}
        </List>

        {/* Menu contextual */}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
        >
          <MenuItem onClick={() => handleActivityAction('view')}>
            <EventIcon sx={{ mr: 1 }} fontSize="small" />
            Ver Detalles
          </MenuItem>
          {selectedActivity?.type === 'approval_required' && (
            <>
              <MenuItem onClick={() => handleActivityAction('approve')}>
                <ApprovalIcon sx={{ mr: 1 }} fontSize="small" />
                Aprobar
              </MenuItem>
              <MenuItem onClick={() => handleActivityAction('reject')}>
                <CancelIcon sx={{ mr: 1 }} fontSize="small" />
                Rechazar
              </MenuItem>
            </>
          )}
          <MenuItem onClick={() => handleActivityAction('contact')}>
            <EmailIcon sx={{ mr: 1 }} fontSize="small" />
            Contactar
          </MenuItem>
        </Menu>

        {/* Estadísticas rápidas */}
        <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
          <Typography variant="caption" color="text.secondary">
            📊 Última hora: {recentActivities.length} actividades • 
            Hoy: {allActivities.filter(a => {
              const today = new Date().toDateString();
              return new Date(a.timestamp).toDateString() === today;
            }).length} eventos
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
};
export default OrganizerActivityWidget;